import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Switch, Spin, message, Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { AnimatePresence } from 'motion/react';
import {
	LuFileText,
	LuSparkles,
	LuBriefcase,
} from 'react-icons/lu';
import { lambda } from 'src/_shared/services/utils.js';
import { darkenHex, accentRgba, Base64 } from './utils.js';
import SkillsModal from './SkillsModal.jsx';
import WorkExperienceModal from './WorkExperienceModal.jsx';

function ActionTile({ icon: Icon, label, onClick, accentColor, disabled }) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className="flex flex-col items-center justify-center gap-2"
			style={{
				flex: 1,
				minWidth: 0,
				padding: '18px 12px',
				borderRadius: '0.75rem',
				border: '1px solid #e2e8f0',
				background: '#fff',
				cursor: disabled ? 'wait' : 'pointer',
				opacity: disabled ? 0.6 : 1,
				transition: 'all 0.2s',
			}}
			onMouseOver={(e) => {
				if (disabled) return;
				e.currentTarget.style.borderColor = accentColor;
				e.currentTarget.style.background = accentRgba(accentColor, 0.04);
			}}
			onMouseOut={(e) => {
				e.currentTarget.style.borderColor = '#e2e8f0';
				e.currentTarget.style.background = '#fff';
			}}
		>
			<div
				className="flex items-center justify-center rounded-lg"
				style={{
					width: 36,
					height: 36,
					background: accentRgba(accentColor, 0.1),
					color: accentColor,
				}}
			>
				{Icon ? <Icon size={18} /> : <Spin size="small" />}
			</div>
			<span
				style={{
					fontSize: 12,
					fontWeight: 500,
					color: '#1e293b',
					textAlign: 'center',
					lineHeight: 1.3,
				}}
			>
				{label}
			</span>
		</button>
	);
}

function mergeResumeIntoDraft(draft, parsedDocument, skillsArray) {
	const next = { ...(draft || {}) };
	const structured = parsedDocument?.Resume?.StructuredXMLResume || {};
	const employerOrgs = structured?.EmploymentHistory?.EmployerOrg || [];

	const newEmployment = [];
	for (const org of employerOrgs) {
		const positions = org?.PositionHistory || [];
		for (const position of positions) {
			const startYM = position?.StartDate?.YearMonth;
			const endYM = position?.EndDate?.YearMonth;
			const endValues = Object.values(position?.EndDate || {});
			const startDate = startYM ? new Date(`${startYM}-02`) : null;
			const endDate = endYM ? new Date(`${endYM}-02`) : null;

			const employment = {
				title: position?.Title || '',
				description: position?.Description || '',
				name: position?.OrgName?.OrganizationName || '',
			};
			if (startDate && !isNaN(startDate)) {
				employment.start = {
					month: startDate.getMonth(),
					year: startDate.getFullYear(),
					monthName: startDate.toLocaleString('default', { month: 'long' }),
					fullString: startDate.toISOString().slice(0, 10),
				};
			}
			if (endDate && !isNaN(endDate)) {
				employment.end = {
					month: endDate.getMonth(),
					year: endDate.getFullYear(),
					monthName: endDate.toLocaleString('default', { month: 'long' }),
					fullString: endDate.toISOString().slice(0, 10),
					current: endValues.includes('current'),
				};
			}
			newEmployment.push(employment);
		}
	}

	const existingEmployment = Array.isArray(next.employment) ? next.employment : [];
	const mergedEmployment = [...existingEmployment];
	for (const job of newEmployment) {
		const matchIndex = mergedEmployment.findIndex(
			(existing) =>
				existing.title === job.title &&
				existing.start?.fullString === job.start?.fullString
		);
		if (matchIndex >= 0) mergedEmployment.splice(matchIndex, 1, job);
		else mergedEmployment.push(job);
	}
	next.employment = mergedEmployment;

	const skillsSet = new Set(
		(Array.isArray(next.skills) ? next.skills : []).map((s) => s.toLowerCase())
	);
	for (const category of skillsArray || []) {
		for (const subtaxonomy of category?.['sov:Subtaxonomy'] || []) {
			for (const skill of subtaxonomy?.['sov:Skill'] || []) {
				const childSkills = skill?.['sov:ChildSkill'];
				if (childSkills) {
					for (const child of childSkills) {
						const name = child?.['@name'];
						if (name) skillsSet.add(name.toLowerCase());
					}
				} else {
					const name = skill?.['@name'];
					if (name) skillsSet.add(name.toLowerCase());
				}
			}
		}
	}
	next.skills = [...skillsSet];

	return next;
}

function CareerProfileCard({
	accentColor,
	draftOpenToRole,
	onChangeOpenToRole,
	draftCareerProfile,
	onChangeCareerProfile,
	isDirty,
	isSaving,
	onUpdateProfile,
	hideOpenToNewRoles,
}) {
	const [skillsModalOpen, setSkillsModalOpen] = useState(false);
	const [workModalOpen, setWorkModalOpen] = useState(false);
	const [importing, setImporting] = useState(false);
	const fileInputRef = useRef(null);

	const skills = draftCareerProfile?.skills || [];
	const employment = draftCareerProfile?.employment || [];

	const parseResume = async (file) => {
		if (!file) return;
		setImporting(true);
		try {
			const revisionDate = new Date(file.lastModified ?? Date.now())
				.toISOString()
				.slice(0, 10);
			const buffer = await file.arrayBuffer();
			const encodedFile = Base64.encodeArray(buffer);
			const data = await lambda({
				endpoint: 'sovrendatacalls',
				variables: {
					DocumentAsBase64String: encodedFile,
					RevisionDate: revisionDate,
				},
			});
			const parsedDocument =
				typeof data.parsedDocument === 'string'
					? JSON.parse(data.parsedDocument)
					: data.parsedDocument;
			const skillsArray =
				parsedDocument?.Resume?.UserArea?.['sov:ResumeUserArea']?.[
					'sov:ExperienceSummary'
				]?.['sov:SkillsTaxonomyOutput']?.['sov:TaxonomyRoot']?.[0]?.[
					'sov:Taxonomy'
				] || [];

			const nextProfile = mergeResumeIntoDraft(
				draftCareerProfile,
				parsedDocument,
				skillsArray
			);
			nextProfile.sovrenResume = parsedDocument;
			nextProfile.resumeFile = {
				name: file.name,
				lastModified: file.lastModified,
				size: file.size,
				type: file.type,
			};
			onChangeCareerProfile(nextProfile);
			message.success('Resume imported — review and click Update Profile');
		} catch (error) {
			console.error('Resume parse failed:', error);
			message.error(
				'There was a problem uploading your resume. Please try again in a few minutes.'
			);
		} finally {
			setImporting(false);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		if (employment.length > 0) {
			Modal.confirm({
				title: 'Importing your resume will update your work history.',
				icon: <InfoCircleOutlined />,
				content:
					'Your current work history may be overwritten or duplicated if you continue.',
				onOk: () => parseResume(file),
			});
		} else {
			parseResume(file);
		}
	};

	const triggerImport = () => fileInputRef.current?.click();

	const handleChangeSkills = (nextSkills) => {
		onChangeCareerProfile({ ...(draftCareerProfile || {}), skills: nextSkills });
	};

	const handleChangeEmployment = (nextEmployment) => {
		onChangeCareerProfile({
			...(draftCareerProfile || {}),
			employment: nextEmployment,
		});
	};

	return (
		<section
			className="rounded-2xl bg-white"
			style={{
				border: '1px solid #f1f5f9',
				padding: 20,
				boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
			}}
		>
			<div
				className="flex flex-wrap items-center justify-between gap-3"
				style={{ marginBottom: 16 }}
			>
				<h2
					style={{
						fontSize: 16,
						fontWeight: 600,
						color: '#1e293b',
						margin: 0,
					}}
				>
					My Career Profile
				</h2>
				{!hideOpenToNewRoles && (
					<div
						className="flex items-center"
						style={{ gap: 12, lineHeight: 1 }}
					>
						<span
							style={{
								fontSize: 13,
								color: '#475569',
								fontWeight: 500,
								lineHeight: 1,
								margin: 0,
								padding: 0,
							}}
						>
							Open to New Roles
						</span>
						<Switch
							checked={draftOpenToRole}
							onChange={onChangeOpenToRole}
							style={draftOpenToRole ? { background: accentColor } : undefined}
						/>
					</div>
				)}
			</div>

			<div className="flex flex-col gap-3 sm:flex-row">
				<ActionTile
					icon={importing ? null : LuFileText}
					label={importing ? 'Importing…' : 'Import Resume'}
					onClick={triggerImport}
					accentColor={accentColor}
					disabled={importing}
				/>
				<ActionTile
					icon={LuSparkles}
					label={`Add Skills${skills.length ? ` (${skills.length})` : ''}`}
					onClick={() => setSkillsModalOpen(true)}
					accentColor={accentColor}
				/>
				<ActionTile
					icon={LuBriefcase}
					label={`Add Work Experience${employment.length ? ` (${employment.length})` : ''}`}
					onClick={() => setWorkModalOpen(true)}
					accentColor={accentColor}
				/>
			</div>

			<input
				ref={fileInputRef}
				type="file"
				accept=".pdf,.txt,.rtf,.doc,.docm,.docx,.dot,.dotm,.dotx,.mht,.mhtml,.odt,.ott,.odm,.htm,.html,.htmlc,.wps,.xml,.xps,.eml,.emlx,.msg,.oft,.olm,.zip,.gzip,.7z"
				onChange={handleFileChange}
				style={{ display: 'none' }}
			/>

			{employment.length > 0 && (
				<div
					className="mt-4 flex flex-col gap-3"
					style={{
						padding: '14px 16px',
						borderRadius: '0.75rem',
						background: '#f8fafc',
						border: '1px solid #f1f5f9',
					}}
				>
					<div className="flex items-center justify-between">
						<span
							style={{
								fontSize: 12,
								fontWeight: 600,
								color: '#475569',
								textTransform: 'uppercase',
								letterSpacing: '0.04em',
							}}
						>
							Work Experience
						</span>
						<button
							type="button"
							onClick={() => setWorkModalOpen(true)}
							style={{
								fontSize: 12,
								fontWeight: 500,
								color: accentColor,
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								padding: 0,
							}}
						>
							View all
						</button>
					</div>
					{employment.slice(0, 3).map((job, i) => {
						const startLabel = job.start
							? `${job.start.monthName?.slice(0, 3)} ${job.start.year}`
							: '';
						const endLabel = job.end?.current
							? 'Present'
							: job.end
								? `${job.end.monthName?.slice(0, 3)} ${job.end.year}`
								: '';
						const dateRange = [startLabel, endLabel]
							.filter(Boolean)
							.join(' – ');

						return (
							<div
								key={job.start?.fullString || i}
								className="flex items-start gap-3"
								style={{
									paddingTop: i > 0 ? 10 : 0,
									borderTop: i > 0 ? '1px solid #e2e8f0' : 'none',
								}}
							>
								<div
									className="mt-0.5 flex shrink-0 items-center justify-center rounded-lg"
									style={{
										width: 32,
										height: 32,
										background: accentRgba(accentColor, 0.1),
										color: accentColor,
									}}
								>
									<LuBriefcase size={15} />
								</div>
								<div className="min-w-0 flex-1">
									<div
										style={{
											fontSize: 13,
											fontWeight: 600,
											color: '#1e293b',
											lineHeight: 1.3,
										}}
									>
										{job.title || 'Untitled Role'}
									</div>
									{job.name && (
										<div
											style={{
												fontSize: 12,
												color: '#475569',
												lineHeight: 1.4,
												marginTop: 1,
											}}
										>
											{job.name}
										</div>
									)}
									{dateRange && (
										<div
											style={{
												fontSize: 11,
												color: '#475569',
												lineHeight: 1.4,
												marginTop: 2,
											}}
										>
											{dateRange}
										</div>
									)}
								</div>
							</div>
						);
					})}
					{employment.length > 3 && (
						<div
							style={{
								fontSize: 12,
								color: '#475569',
								textAlign: 'center',
								paddingTop: 4,
							}}
						>
							+{employment.length - 3} more
						</div>
					)}
				</div>
			)}

			<button
				type="button"
				onClick={onUpdateProfile}
				disabled={!isDirty || isSaving}
				className="mt-4 flex w-full items-center justify-center gap-2"
				style={{
					padding: '12px 16px',
					borderRadius: '0.75rem',
					background: isDirty ? accentColor : '#e2e8f0',
					color: isDirty ? '#fff' : '#475569',
					border: 'none',
					fontWeight: 600,
					fontSize: 14,
					cursor: isDirty && !isSaving ? 'pointer' : 'not-allowed',
					transition: 'background 0.2s, color 0.2s',
				}}
				onMouseOver={(e) => {
					if (isDirty && !isSaving) {
						e.currentTarget.style.background = darkenHex(accentColor);
					}
				}}
				onMouseOut={(e) => {
					if (isDirty && !isSaving) {
						e.currentTarget.style.background = accentColor;
					}
				}}
			>
				{isSaving && <Spin size="small" />}
				Update Profile
			</button>

			{createPortal(
				<AnimatePresence>
					{skillsModalOpen && (
						<SkillsModal
							onClose={() => setSkillsModalOpen(false)}
							skills={skills}
							onChangeSkills={handleChangeSkills}
							accentColor={accentColor}
						/>
					)}
				</AnimatePresence>,
				document.body
			)}

			{createPortal(
				<AnimatePresence>
					{workModalOpen && (
						<WorkExperienceModal
							onClose={() => setWorkModalOpen(false)}
							employment={employment}
							onChangeEmployment={handleChangeEmployment}
							accentColor={accentColor}
						/>
					)}
				</AnimatePresence>,
				document.body
			)}
		</section>
	);
}

export default CareerProfileCard;
